import { useEffect, useState } from 'react';
import { XClose } from "../icons/XClose";
import { TextComponent } from "./TextComponent";
import { Column } from "./Column";
import { ProfileInfo } from "../../features/profile/presentation/interfaces/EditProfileInterface";
import { CameraPrivate, CameraPublic } from "../icons/Camera";
import { compressImage } from "../constants/constants";

interface ImageUploaderProps {
    file?: File | null;
    files?: File[] | null;
    setFile?: (file: File | null) => void;
    setFiles?: (files: File[] | null) => void;
    defaultImage?: string;
    maxSize?: number;
    className?: string;
    inputId: string;
    multiple?: boolean;
    columns?: number;
    slots?: number;
    iProfileInfo?: ProfileInfo[];
    setIProfileInfo?: React.Dispatch<React.SetStateAction<ProfileInfo | undefined>>;
    handleViewEditImage?: (index: number) => void;
    loading?: boolean;
    showOnboardingSourceButtons?: boolean;
    cropToSquareOnUpload?: boolean;
}

export const ImageUploader = ({
    file,
    files,
    setFile,
    setFiles,
    defaultImage,
    maxSize = 5,
    className,
    inputId = "inputFile",
    multiple = false,
    columns = 1,
    slots = 1,
    iProfileInfo,
    setIProfileInfo,
    handleViewEditImage,
    loading = false,
    showOnboardingSourceButtons = false,
    cropToSquareOnUpload = false,
}: ImageUploaderProps) => {
    const totalItems = (iProfileInfo?.length ?? 0) + (files?.length ?? 0);
    const [error, setError] = useState<string>("");
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [arrastrandoImagen, setArrastrandoImagen] = useState<boolean>(false);
    const maxSizeInBytes = maxSize * 1024 * 1024;
    const filetypes = /jpeg|jpg|png|webp|heic|heif/;
    const galleryInputId = `${inputId}-gallery`;
    const cameraInputId = `${inputId}-camera`;

    const cropSquareImage = async (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            const sourceUrl = URL.createObjectURL(file);
            const image = new Image();
            image.onload = () => {
                const size = Math.min(image.naturalWidth, image.naturalHeight);
                const sx = Math.max(0, Math.floor((image.naturalWidth - size) / 2));
                const sy = Math.max(0, Math.floor((image.naturalHeight - size) / 2));
                const canvas = document.createElement("canvas");
                canvas.width = size;
                canvas.height = size;
                const context = canvas.getContext("2d");
                if (!context) {
                    URL.revokeObjectURL(sourceUrl);
                    reject(new Error("Image crop context unavailable"));
                    return;
                }
                context.drawImage(image, sx, sy, size, size, 0, 0, size, size);
                canvas.toBlob((blob) => {
                    URL.revokeObjectURL(sourceUrl);
                    if (!blob) {
                        reject(new Error("Image crop failed"));
                        return;
                    }
                    const normalizedName = file.name.replace(/\.[^/.]+$/, ".jpg");
                    resolve(new File([blob], normalizedName, { type: "image/jpeg" }));
                }, "image/jpeg", 0.92);
            };
            image.onerror = () => {
                URL.revokeObjectURL(sourceUrl);
                reject(new Error("Image crop failed"));
            };
            image.src = sourceUrl;
        });
    };

    useEffect(() => {
        if (!file) {
            setFileUrl(null);
            return;
        }

        const filePreview = URL.createObjectURL(file);
        setFileUrl(filePreview);

        return () => {
            URL.revokeObjectURL(filePreview);
        };
    }, [file]);

    const handleDragEnter = () => {
        setArrastrandoImagen(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;
        if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
            setArrastrandoImagen(false);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const droppedFiles = Array.from(event.dataTransfer.files);
        setArrastrandoImagen(false);
        processFiles(droppedFiles);
    };

    const processFiles = async (uploadedFiles: File[]) => {
        let errorMessage: string | null = null;
        const validFiles: File[] = [];
        for (const file of uploadedFiles) {
            if (!file.type.startsWith("image/") || !filetypes.test(file.name.toLowerCase())) {
                errorMessage = "Only image files are allowed (jpg, jpeg, png, webp, heic, heif)";
            } else if (file.size > maxSizeInBytes) {
                errorMessage = `File size should not exceed ${maxSize}MB`;
            } else {
                try {
                    const compressedFile = await compressImage(file, {
                        quality: 0.9,
                        maxWidth: 1600,
                        maxHeight: 1600,
                    });
                    if (!cropToSquareOnUpload) {
                        validFiles.push(compressedFile);
                        continue;
                    }

                    try {
                        const squareFile = await cropSquareImage(compressedFile);
                        validFiles.push(squareFile);
                    } catch {
                        validFiles.push(compressedFile);
                    }
                } catch (error) {
                    console.error("Compression error:", error);
                    const lowerName = file.name.toLowerCase();
                    const isSpecialFormat = lowerName.endsWith(".heic") || lowerName.endsWith(".heif") || lowerName.endsWith(".webp");
                    if (isSpecialFormat) {
                        errorMessage = "This image format is not supported on this browser. Please try a jpg or png file.";
                    } else {
                        validFiles.push(file);
                    }
                }
            }
        }

        if (errorMessage) {
            if (setError) setError(errorMessage);
            return;
        }

        if (setError) setError("");

        if (multiple && setFiles) {
            const currentFiles = files || [];
            const availableSlots = slots! - totalItems;

            if (validFiles.length > availableSlots) {
                validFiles.splice(availableSlots);
            }

            const updatedFiles = [...currentFiles, ...validFiles];
            setFiles(updatedFiles);
        } else if (setFile) {
            setFile(validFiles[0] || null);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
        processFiles(selectedFiles);
    };

    const handleDelete = (index?: number) => {
        if (multiple && files && setFiles) {
            const updatedFiles = [...files];
            updatedFiles.splice(index!, 1);
            setFiles(updatedFiles);
        } else if (setFile) {
            setFile(null);
        }
    };

    const handleState = (index: number, state: number) => {
        if (iProfileInfo && setIProfileInfo) {
            setIProfileInfo({
                ...iProfileInfo[index],
                state,
            });
        }
    }

    const handleStateClick = (event: React.MouseEvent<HTMLButtonElement>, index: number, state: number) => {
        event.preventDefault();
        event.stopPropagation();
        handleState(index, state);
    }

    const handleStatePress = (event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
        event.stopPropagation();
    }

    const UploadPhotoDrag = () => {
        return (
            <label
                className={`flex flex-col text-center justify-center items-center w-full h-full border-2 bg-socondary ${arrastrandoImagen ? "border-primary" : "border-pink"} ${iProfileInfo && "order-first"} border-dashed p-5 bg-secondary rounded-sm`}
                htmlFor={inputId}
            >
                <img
                    src="/picture.svg"
                    width={83}
                    height={61}
                    draggable={false}
                    alt="image-default"
                />
                {loading ? (
                    <TextComponent variant="h6">
                        Processing your <span className="text-primary">pictures</span>, please wait...
                    </TextComponent>
                ) : (
                    <TextComponent variant="h6">
                        Drop your picture to add or <span className="text-primary">browse</span>
                    </TextComponent>
                )}
            </label>
        )
    }

    return (
        <picture
            className={`flex relative justify-center items-center ${className}`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            onDragEnter={handleDragEnter}
        >
            {multiple ? (
                <Column
                    mainAxisAlignment="center"
                    crossAxisAlignment="center"
                    className={`gap-5 ${!iProfileInfo && "md:gap-10"} w-full`}
                >
                    <div className={`grid grid-cols-2 md:grid-cols-3 gap-5 w-full sm:gap-5 place-items-center`}>
                        {(iProfileInfo && iProfileInfo.length > 0 ? iProfileInfo : files)?.map((item, index) => (
                            <div key={index} className="relative w-full h-24 md:h-28  bg-gray-300 rounded-xl">
                                <img
                                    className={`${iProfileInfo && "cursor-pointer hover:opacity-95"} object-cover w-full h-full rounded-xl`}
                                    src={iProfileInfo ? (item as ProfileInfo).url : URL.createObjectURL(item as File)}
                                    {...(iProfileInfo && handleViewEditImage ? { onClick: () => { handleViewEditImage(index) } } : {})}
                                    alt={`image-${index}`}
                                />
                                {index !== 0 && (
                                    <>
                                        {!iProfileInfo &&
                                            <button
                                                onClick={() => handleDelete(index)}
                                                className="absolute top-2 right-2 text-grayLight"
                                                aria-label="Remove Image"
                                            >
                                                <XClose />
                                            </button>
                                        }
                                        {iProfileInfo && (
                                            <button
                                                type="button"
                                                onMouseDown={handleStatePress}
                                                onTouchStart={handleStatePress}
                                                onClick={(event) => handleStateClick(event, index, (item as ProfileInfo).state === 0 ? 1 : 0)}
                                                className="absolute bottom-2 right-2 text-white"
                                                aria-label={`${(item as ProfileInfo).state === 0 ? "Public Image" : "Private Image"}`}
                                            >
                                                {(item as ProfileInfo).state === 0 ? <CameraPrivate /> : < CameraPublic />}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                        {Array.from({ length: slots - ((iProfileInfo?.length || files?.length) || 0) }).map((_, index) => (
                            <div key={index} className="w-full h-[5.5rem] md:h-28 bg-gray-300 rounded-xl"></div>
                        ))}
                    </div>
                    {error &&
                        <TextComponent variant="h7" className="text-red-500 text-center">
                            {error}
                        </TextComponent>
                    }
                    {showOnboardingSourceButtons && totalItems < slots && (
                        <div className="grid grid-cols-2 gap-3 w-full">
                            <label
                                htmlFor={cameraInputId}
                                className="cursor-pointer rounded-lg border border-pink bg-socondary py-2 text-center text-primary font-semibold"
                            >
                                Camera
                            </label>
                            <label
                                htmlFor={galleryInputId}
                                className="cursor-pointer rounded-lg border border-pink bg-socondary py-2 text-center text-primary font-semibold"
                            >
                                Gallery
                            </label>
                        </div>
                    )}
                    {totalItems < slots ? (
                        <>
                            {UploadPhotoDrag()}
                        </>
                    ) : (
                        <Column className={`w-full rounded-sm border border-dashed border-pink bg-socondary py-4 ${iProfileInfo && "order-first"} `}>
                            <span className="text-primary text-center">
                                You have reached the limit of {slots} photos.
                            </span>
                        </Column>
                    )}
                </Column>
            ) : (
                <>
                    {fileUrl ? (
                        <img
                            className="object-cover w-full h-full rounded-3xl"
                            src={fileUrl}
                            alt="preview-image"
                        />
                    ) : (defaultImage ? (
                            <label htmlFor={inputId}>
                                <img
                                    src={defaultImage}
                                    className="object-cover w-full h-full rounded-3xl"
                                    alt="image-default"
                                    width={208}
                                    height={198}
                                />
                            </label>
                        ):(
                           <>
                                {UploadPhotoDrag()}
                           </>
                        )
                    )}
                    {showOnboardingSourceButtons && (
                        <div className="absolute -bottom-14 left-1/2 grid w-[210px] -translate-x-1/2 grid-cols-2 gap-2">
                            <label
                                htmlFor={cameraInputId}
                                className="cursor-pointer rounded-full border border-pink bg-white px-3 py-1 text-center text-sm font-semibold text-primary"
                            >
                                Camera
                            </label>
                            <label
                                htmlFor={galleryInputId}
                                className="cursor-pointer rounded-full border border-pink bg-white px-3 py-1 text-center text-sm font-semibold text-primary"
                            >
                                Gallery
                            </label>
                        </div>
                    )}
                </>
            )}
            <input
                onChange={handleChange}
                className="absolute w-10 hidden"
                type="file"
                id={inputId}
                accept=".jpeg, .jpg, .png, .webp, .heic, .heif"
                multiple={multiple}
                disabled={loading}
            />
            <input
                onChange={handleChange}
                className="hidden"
                type="file"
                id={galleryInputId}
                accept=".jpeg, .jpg, .png, .webp, .heic, .heif"
                multiple={multiple}
                disabled={loading}
            />
            <input
                onChange={handleChange}
                className="hidden"
                type="file"
                id={cameraInputId}
                accept="image/*"
                capture="environment"
                multiple={multiple}
                disabled={loading}
            />
        </picture>
    );
};
